import { useTokenExpiration } from '../composables/useTokenExpiration'
import { useAuthStore } from '../stores/auth'
import logger from '../utils/logger'
import { getCsrfToken } from '../utils/csrfToken'
import { isTeacherSession } from '../utils/isTeacherSession'
import { resolveSessionCountdownSeconds } from '../utils/sessionExpiresIn'

export class TokenExpirationService {
  private checkTimer: ReturnType<typeof setTimeout> | null = null
  private readonly BASE_CHECK_INTERVAL = 60 * 1000 // 60s baseline check
  private readonly WARNING_CHECK_INTERVAL = 10 * 1000 // 10s checks inside warning window
  private readonly WARNING_THRESHOLD_SECONDS = 120 // Warn 2 minutes (120 seconds) before expiration
  private warningShown = false // Track if warning modal has been shown for current expiration cycle
  private expiredShown = false // Track if expired modal has been shown for current expiration cycle
  private started = false
  private checkInProgress = false

  startTokenExpirationCheck() {
    // Idempotent start: avoid double intervals / double immediate checks
    if (this.started) return
    this.started = true

    logger.debug(`Token expiration service started. Baseline check every ${this.BASE_CHECK_INTERVAL / 1000} seconds`)
    
    // Check immediately on start
    this.checkTokenExpiration()
  }

  stopTokenExpirationCheck() {
    this.started = false
    if (this.checkTimer) {
      clearTimeout(this.checkTimer)
      this.checkTimer = null
    }
    // Hide any visible modal when stopping the service
    const { hideWarning } = useTokenExpiration()
    hideWarning()
    this.warningShown = false
    this.expiredShown = false
  }

  private scheduleNextCheck(ms: number) {
    if (!this.started) return
    if (this.checkTimer) clearTimeout(this.checkTimer)
    this.checkTimer = setTimeout(() => this.checkTokenExpiration(), ms)
  }

  private isAuthenticated(): boolean {
    // Check if user is authenticated before showing any modals
    try {
      const authStore = useAuthStore()
      return authStore.isAuthenticated || localStorage.getItem('isAuthenticated') === 'true'
    } catch {
      // Fallback to localStorage check if store is not available
      return localStorage.getItem('isAuthenticated') === 'true'
    }
  }

  private async checkTokenExpiration() {
    // Prevent overlapping checks (multiple callers / slow network)
    if (this.checkInProgress) return
    this.checkInProgress = true
    // CRITICAL: Only check token expiration if user is authenticated
    if (!this.isAuthenticated()) {
      logger.debug('User is not authenticated - skipping token expiration check')
      // Hide any visible modal if user is not authenticated
      const { hideWarning } = useTokenExpiration()
      hideWarning()
      this.warningShown = false
      this.expiredShown = false
      this.checkInProgress = false
      this.scheduleNextCheck(this.BASE_CHECK_INTERVAL)
      return
    }
    // Teachers (OTP): no proactive "session expiring" / "session expired" modals
    if (isTeacherSession()) {
      this.checkInProgress = false
      this.scheduleNextCheck(this.BASE_CHECK_INTERVAL)
      return
    }
    // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
    // We can't check expiration proactively, but the backend will return 401 when expired
    // The reactive refresh mechanism in api.ts will handle token refresh automatically
    // This service can check session status via API call if needed
    
    // Check if user is still authenticated by making a lightweight API call
    try {
      logger.debug('Checking token expiration status')
      const response = await fetch('/api/sessions/token-status', {
        method: 'GET',
        credentials: 'include' // SECURITY: Required for HTTP-only cookies
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data?.success && data?.data?.tokenExpiration) {
          const expirationInfo = data.data.tokenExpiration
          const expiresIn = typeof expirationInfo.expiresIn === 'number' ? expirationInfo.expiresIn : null
          
          // Reset flags if token is refreshed and no longer in warning/expired state
          if (
            (this.warningShown || this.expiredShown) &&
            expiresIn !== null &&
            expiresIn > this.WARNING_THRESHOLD_SECONDS
          ) {
            this.warningShown = false
            this.expiredShown = false
          }
          
          // If token expires within warning threshold, show warning once
          if (expiresIn !== null && 
              expiresIn <= this.WARNING_THRESHOLD_SECONDS && // 2 minutes
              expiresIn > 0 && 
              !expirationInfo.isExpired &&
              !this.warningShown) {
            // CRITICAL: Double-check authentication before showing modal
            if (!this.isAuthenticated()) {
              logger.debug('User is not authenticated - skipping token expiration warning')
              return
            }

            logger.debug('Token expiration warning triggered', {
              expiresIn: expirationInfo.expiresIn,
              threshold: this.WARNING_THRESHOLD_SECONDS,
              isExpired: expirationInfo.isExpired
            })
            this.warningShown = true

            const { showTokenExpirationWarning } = useTokenExpiration()

            showTokenExpirationWarning({
              title: 'Session Expiring Soon',
              message: 'Your session will expire soon. Would you like to continue your session?',
              continueText: 'Yes, Continue',
              logoutText: 'No, Logout',
              countdownSeconds: expiresIn,
              onContinue: async () => {
                logger.debug('User clicked Continue - refreshing token')
                this.warningShown = false
                this.expiredShown = false
                await this.refreshToken()
              },
              onLogout: () => {
                logger.info('User clicked Logout')
                this.warningShown = false
                this.expiredShown = false
                this.logout()
              }
            })
          }
          
          // If token is already expired, show expired message once
          if (
            (expirationInfo.isExpired || (expiresIn !== null && expiresIn <= 0)) &&
            !this.expiredShown
          ) {
            // CRITICAL: Double-check authentication before showing modal
            if (!this.isAuthenticated()) {
              logger.debug('User is not authenticated - skipping expired token modal')
              return
            }

            logger.warn('Token already expired - showing expired modal')
            this.expiredShown = true
            const { showTokenExpirationWarning } = useTokenExpiration()

            showTokenExpirationWarning({
              title: 'Session Expired',
              message: 'Your session has expired. Would you like to continue your session or logout?',
              continueText: 'Yes, Continue',
              logoutText: 'No, Logout',
              countdownSeconds: resolveSessionCountdownSeconds(expiresIn),
              onContinue: async () => {
                logger.debug('User clicked Continue - refreshing token')
                this.warningShown = false
                this.expiredShown = false
                await this.refreshToken()
              },
              onLogout: () => {
                logger.info('User clicked Logout')
                this.warningShown = false
                this.expiredShown = false
                this.logout()
              }
            })
          }

          // Adaptive cadence: check more frequently inside warning window.
          // This makes the warning reliable without hammering the endpoint all the time.
          if (expiresIn !== null && expiresIn <= this.WARNING_THRESHOLD_SECONDS && expiresIn > 0) {
            this.scheduleNextCheck(this.WARNING_CHECK_INTERVAL)
          } else {
            this.scheduleNextCheck(this.BASE_CHECK_INTERVAL)
          }
          return
        }
      } else if (response.status === 401) {
        // Token is invalid or missing - treat as expired
        logger.debug('Token status check returned 401 - token may be expired or invalid')
        // Don't show warning here - let the reactive refresh mechanism handle it
        // The 401 will trigger token refresh in api.ts interceptor
      }
    } catch (error) {
      // Silently fail - reactive refresh will handle expired tokens
      logger.debug('Failed to check token expiration:', error)
    } finally {
      this.checkInProgress = false
    }

    // Default cadence when response was unexpected / no expiration info available
    this.scheduleNextCheck(this.BASE_CHECK_INTERVAL)
  }

  private async refreshToken() {
    try {
      // SECURITY: Refresh token (access token is now in HTTP-only cookie)
      const csrf = getCsrfToken()
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf
        },
        credentials: 'include' // SECURITY: Required for HTTP-only cookies
      })

      if (response.ok) {
        const data = await response.json()
        // SECURITY: Access token is in HTTP-only cookie, not in response
        if (data?.success && data?.data?.csrf) {
          localStorage.setItem('csrf', data.data.csrf)
          logger.info('Token refreshed successfully')
          this.warningShown = false
          this.expiredShown = false
        }
      } else {
        throw new Error('Token refresh failed')
      }
    } catch (error) {
      logger.error('Failed to refresh token:', error)
      this.logout()
    }
  }

  private logout() {
    // Keep Pinia and localStorage in sync so session modal hides (OR logic was leaving UI stuck)
    try {
      useAuthStore().clearAuthState()
    } catch {
      localStorage.removeItem('csrf')
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('currentUser')
    }
    window.location.href = '/login'
  }
}

// Export singleton instance
export const tokenExpirationService = new TokenExpirationService()
